"use client";

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useSupabase } from "@/hooks/useSupabase";
import { toast } from "sonner";
import {
  SolicitacaoRastreioForm,
  type SolicitacaoRastreioState,
} from "@/components/auditoria-correcao/SolicitacaoRastreioForm";
import {
  ORC_CORRECAO_FIELDS_PRIMARY,
  ORC_CORRECAO_FIELDS_SECONDARY,
  ORC_CORRIGIR_RPC,
  ORC_FIELD_LABELS,
  type OrcCorrigirResult,
  type OrcPatchableField,
} from "@/lib/orcamentoHistorico";

export interface OrcamentoLinhaParaCorrigir {
  id: string;
  mes: string | null;
  mes_competencia: string | null;
  data: string | null;
  valor_imposto: number | null;
  valor: number;
  historico: string | null;
  centro_resultado: string | null;
  tipo_receita_despesa: string | null;
  classificacao_receita_despesa: string | null;
  descricao_receita_despesa: string | null;
  cliente_fornecedor: string | null;
  pti: string | null;
  tipo_td: string | null;
  classificacao_td: string | null;
  area: string | null;
  sub_area: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: OrcamentoLinhaParaCorrigir | null;
  onSuccess?: () => void;
}

const NUMERIC_FIELDS = new Set<OrcPatchableField>(["valor", "valor_imposto"]);

function fieldValue(item: OrcamentoLinhaParaCorrigir, key: OrcPatchableField): string {
  const raw = item[key as keyof OrcamentoLinhaParaCorrigir];
  if (raw === null || raw === undefined) return "";
  if (key === "data" && typeof raw === "string") return raw.slice(0, 10);
  return String(raw);
}

function CorrigirField({
  fieldKey,
  draft,
  setDraft,
}: {
  fieldKey: OrcPatchableField;
  draft: Partial<Record<OrcPatchableField, string>>;
  setDraft: React.Dispatch<React.SetStateAction<Partial<Record<OrcPatchableField, string>>>>;
}) {
  const isDate = fieldKey === "data";
  const isNumeric = NUMERIC_FIELDS.has(fieldKey);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`orc-${fieldKey}`} className="text-xs">
        {ORC_FIELD_LABELS[fieldKey]}
      </Label>
      <Input
        id={`orc-${fieldKey}`}
        type={isDate ? "date" : "text"}
        inputMode={isNumeric ? "decimal" : undefined}
        value={draft[fieldKey] ?? ""}
        onChange={(e) => setDraft((d) => ({ ...d, [fieldKey]: e.target.value }))}
        className="h-9"
      />
    </div>
  );
}

const EMPTY_RASTREIO: SolicitacaoRastreioState = {
  solicitadoPor: "",
  canal: "email",
  referencia: "",
  solicitadoEm: "",
  motivo: "",
  observacoes: "",
};

export function OrcamentoCorrigirLinhaDialog({ open, onOpenChange, item, onSuccess }: Props) {
  const { supabase } = useSupabase();
  const [rastreio, setRastreio] = useState<SolicitacaoRastreioState>(EMPTY_RASTREIO);
  const [draft, setDraft] = useState<Partial<Record<OrcPatchableField, string>>>({});
  const [saving, setSaving] = useState(false);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    const initial: Partial<Record<OrcPatchableField, string>> = {};
    const all = [...ORC_CORRECAO_FIELDS_PRIMARY, ...ORC_CORRECAO_FIELDS_SECONDARY];
    for (const k of all) {
      initial[k] = fieldValue(item, k);
    }
    setDraft(initial);
    setRastreio({ ...EMPTY_RASTREIO, canal: "email" });
    setShowMore(false);
  }, [open, item]);

  const patch = useMemo(() => {
    if (!item) return {};
    const out: Record<string, string | number> = {};
    const keys = [...ORC_CORRECAO_FIELDS_PRIMARY, ...(showMore ? ORC_CORRECAO_FIELDS_SECONDARY : [])];
    for (const k of keys) {
      const novo = draft[k] ?? "";
      const antigo = fieldValue(item, k);
      if (novo === antigo) continue;
      if (NUMERIC_FIELDS.has(k)) {
        const n = novo.trim() === "" ? null : Number(novo.replace(/\./g, "").replace(",", "."));
        if (novo.trim() !== "" && Number.isNaN(n)) continue;
        out[k] = n as number;
      } else {
        out[k] = novo;
      }
    }
    return out;
  }, [item, draft, showMore]);

  const hasChanges = Object.keys(patch).length > 0;

  const handleSave = async () => {
    if (!item) return;
    if (!rastreio.solicitadoPor.trim()) {
      toast.error("Informe quem solicitou o ajuste.");
      return;
    }
    if (!rastreio.canal) {
      toast.error("Selecione por onde veio a solicitação.");
      return;
    }
    if (!rastreio.observacoes.trim() && !rastreio.motivo.trim()) {
      toast.error("Informe observações ou um resumo do motivo.");
      return;
    }
    if (!hasChanges) {
      toast.error("Altere ao menos um campo antes de salvar.");
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase.rpc(ORC_CORRIGIR_RPC, {
        p_id: item.id,
        p_patch: patch,
        p_motivo: rastreio.motivo.trim() || rastreio.observacoes.trim().slice(0, 200),
        p_solicitado_por: rastreio.solicitadoPor.trim(),
        p_canal: rastreio.canal,
        p_referencia_solicitacao: rastreio.referencia.trim() || null,
        p_observacoes: rastreio.observacoes.trim() || rastreio.motivo.trim(),
        p_solicitado_em: rastreio.solicitadoEm ? new Date(rastreio.solicitadoEm).toISOString() : null,
      });
      if (error) throw error;
      const result = data as OrcCorrigirResult;
      if (!result?.success) {
        throw new Error(result?.error || "Falha ao corrigir registro");
      }
      toast.success("Correção registrada no histórico do orçado.");
      onOpenChange(false);
      onSuccess?.();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erro ao salvar correção";
      if (msg.includes("does not exist") || msg.includes("schema cache")) {
        toast.error("RPC de correção não encontrada. Aplique a migração de auditoria do orçado no Supabase.");
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Corrigir linha do orçado</DialogTitle>
          <DialogDescription>
            Ajuste classificação, centro, área ou valor com rastreio de quem solicitou (ex.: e-mail do financeiro).
          </DialogDescription>
        </DialogHeader>

        {item ? (
          <div className="space-y-4 py-2">
            <SolicitacaoRastreioForm
              idPrefix="orc"
              state={rastreio}
              onChange={(p) => setRastreio((s) => ({ ...s, ...p }))}
            />

            <Separator />

            <p className="text-xs text-muted-foreground font-mono break-all">Linha: {item.id}</p>

            <div className="grid gap-3 sm:grid-cols-2">
              {ORC_CORRECAO_FIELDS_PRIMARY.map((key) => (
                <CorrigirField key={key} fieldKey={key} draft={draft} setDraft={setDraft} />
              ))}
            </div>

            <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => setShowMore(!showMore)}>
              {showMore ? "Ocultar campos adicionais" : "Mais campos (mês, fornecedor, TD…)"}
            </Button>

            {showMore ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {ORC_CORRECAO_FIELDS_SECONDARY.map((key) => (
                  <CorrigirField key={key} fieldKey={key} draft={draft} setDraft={setDraft} />
                ))}
              </div>
            ) : null}

            {hasChanges ? (
              <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                <p className="font-medium text-foreground">Campos que serão alterados:</p>
                <ul className="list-disc pl-4 text-muted-foreground">
                  {Object.keys(patch).map((k) => (
                    <li key={k}>{ORC_FIELD_LABELS[k as OrcPatchableField] ?? k}</li>
                  ))}
                </ul>
                {(patch.area || patch.sub_area || patch.centro_resultado || patch.tipo_receita_despesa) ? (
                  <p className="text-muted-foreground pt-1">
                    Centro/tipo no Pipefy serão re-vinculados automaticamente após salvar.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving || !item}>
            {saving ? "Salvando…" : "Salvar correção"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
